import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, AfterViewInit, SimpleChange, SimpleChanges, input, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { PDFFormConfig, KeyValuePair, PDFFieldMapConfig, PDFCheckBoxField, PDFDiffChanges, PDFSignatureCoordinates } from '../../interfaces/config';
import { ViewportService } from 'src/app/services/view-port.service';
import { PdfImageService } from 'src/app/services/pdf-image.service';
import { ReferralDataService } from 'src/app/services/referral-data.service';
import { Router } from '@angular/router';
import { UserService } from 'src/app/services/user.service';
import { EditpdfValidationService } from 'src/app/services/editpdf-validation.service';
import { PROVIDER_TYPES } from 'src/app/shared/constants/static-data.constants';
import { PDF_TABLE_WIDGETS } from 'src/app/shared/constants/pdf-table.constants';
import { parseEncodedJsonArray } from 'src/app/shared/utils/base64.util';
import { StorageService } from 'src/app/services/storage.service';

@Component({
  selector: 'ppl-editpdf',
  standalone: true,
  imports: [
    NgxExtendedPdfViewerModule,
    CommonModule
  ],
  templateUrl: './editpdf.component.html',
  styleUrls: ['./editpdf.component.css']
})
export class EditpdfComponent implements OnInit, AfterViewInit, OnDestroy {
  private pendingBlobUrlForCleanup?: string;

  @Input() pdfConfig?: PDFFormConfig;
  @Input() pdfFile?: string;
  @Input() readonly?: boolean = false;
  @Input() showSignature?: boolean = true;
  @Input() pdfBlob?: Blob;
  @Input() isParticipantFlow?: boolean;
  @Input() isSelf?: boolean;
  @Input() selectedParticipantId?: number;
  @Input() selectedProviderType?: string;
  @Input() isPdfSubmitted?: boolean;
  @Input() highlightedFields?: PDFDiffChanges[] = [];
  @Input() ignorePartBValidationOfI9: boolean = true;
  @Input() resignRequired: boolean = true;
  @Input() SignOnly: boolean = false;

  // this signature and signature id is used when we need to place sign here button on pdf, once user click on sign here button we will get the signature image from parent component and place it on pdf
  @Input() signature?: string;
  @Input() signatureId?: string;

  @Input() QRCodeData?: any;
  programCode?: string;


  signedField: any;

  @Output() fieldsUpdated = new EventEmitter();
  isLoaded = false;
  customError: string = '';

  form?: FormGroup;
  formKeyMapping: KeyValuePair = {};
  formBusinessMapping: KeyValuePair = {};
  formFieldPrepulatedMapping: KeyValuePair = {};
  formFieldRequiredMapping: KeyValuePair = {};
  showErrors = false;
  errors: any = [];
  checkBoxFields: Record<string, any> = [];
  radioBoxFields: Record<string, any> = [];
  processedFields: string[] = [];
  /** Per-cell widget name → text, expanded from the tables described by PDF_TABLE_WIDGETS. */
  private tableCellValues: KeyValuePair = {};
  concernedInputs = ['State', 'line 1', 'line 2', 'line 3', 'line 4', 'line 5', 'Date'];
  signField: any;
  signCordinates: any;
  isSignButtonPlaced: boolean = false;
  public minZoom = 1;
  public zoom = 100;
  public maxZoom = 1;
  zoomLevel: number = 100;
  showOtherSignatures: boolean = false;

  constructor(private fb: FormBuilder, private router: Router, public viewport: ViewportService,
    private pdfImageService: PdfImageService, private referralDataService: ReferralDataService,
    private userService: UserService, private validationService: EditpdfValidationService,
  private storageService: StorageService) {
  }

  ngOnDestroy(): void {
    this.scheduleBlobUrlCleanup(this.pdfFile);
    this.pdfFile = undefined;
    this.pdfBlob = undefined;
  }

  private scheduleBlobUrlCleanup(blobUrl?: string): void {
    if (!blobUrl?.startsWith('blob:')) {
      return;
    }

    this.pendingBlobUrlForCleanup = blobUrl;
    setTimeout(() => {
      if (this.pendingBlobUrlForCleanup) {
        URL.revokeObjectURL(this.pendingBlobUrlForCleanup);
        this.pendingBlobUrlForCleanup = undefined;
      }
    }, 0);
  }

  ngOnInit(): void {
    this.programCode = this.userService.getSelectedProgram()?.program_code ?? '';
    this.showOtherSignatures = this.storageService.getShowOtherSignatures() ?? false;
    if (this.viewport.isMobileView()) {
      this.minZoom = 0.4;
      this.maxZoom = 1.0;
      this.zoom = 40;
      this.zoomLevel = 40;
    }
    const config: KeyValuePair = {};

    if (this.pdfConfig) {
      this.pdfConfig?.fields.forEach((field: PDFFieldMapConfig) => {
        let isFieldRequired = false;

        if (this.isParticipantFlow) {
          isFieldRequired = field.source == 'PTC' ? field.mandatory : false;
        }
        else if (this.isSelf) {
          isFieldRequired = field.mandatory;
        }
        else if (field.source == '' || field.source == this.selectedProviderType) {
          isFieldRequired = field.mandatory;
        }

        if (field.fieldType == "PDCheckBox") {
          this.initCheckBoxes(config, field, isFieldRequired);
        }
        else if (field.fieldType == "PDRadioButton") {
          this.initRadioButtons(config, field, isFieldRequired);
        }
        else {
          if (field.businessName == "todays_date_sec2" && !field.value) {
            field.value = this.getTodayDate();
          }

          if (PDF_TABLE_WIDGETS[field.businessName]) {
            this.expandTableIntoCells(field);
          }

          const id = field.businessName;
          this.formKeyMapping[field.fieldId] = id;
          this.formBusinessMapping[id] = id;
          this.formFieldPrepulatedMapping[field.fieldId] = field.isPrepopulated;
          this.formFieldRequiredMapping[field.fieldId] = isFieldRequired;
          config[id] = [field.value, isFieldRequired ? Validators.required : null]
        }
      });
    }

    console.log('form initialized');

    this.form = this.fb.group(config);
    this.form.valueChanges.subscribe(fld => {
      if (!this.readonly && this.isPdfSubmitted && !this.isSignButtonPlaced) {
        if (this.resignRequired) {
          this.form?.get(this.signField.businessName)?.setValue(undefined, {
            emitEvent: false
          });
        }

        if (this.resignRequired) {
          this.addSignHereButton(this.signCordinates, this.signField);
        }
      }
      this.validateForm();
      this.fieldsUpdated.emit({ data: fld, errors: this.errors, customError: this.customError });
    })
  }


  onZoomChange(newZoom: string | number): void {
    if (typeof newZoom === 'string') {
      this.zoomLevel = parseFloat(newZoom);
    } else {
      this.zoomLevel = newZoom;
    }
    this.updateButtonPosition();
  }

  updateButtonPosition() {
    if (this.signCordinates && this.viewport.isMobileView()) {
      let factor = this.zoomLevel / 100;
      if (this.zoomLevel > 40 && this.zoomLevel <= 50) {
        factor = factor + 0.05;
      }
      else if (this.zoomLevel > 50 && this.zoomLevel <= 60) {
        factor = factor + 0.09;
      }
      else if (this.zoomLevel > 60 && this.zoomLevel <= 70) {
        factor = factor + 0.12;
      }
      else if (this.zoomLevel > 70 && this.zoomLevel <= 80) {
        factor = factor + 0.15;
      }
      else if (this.zoomLevel > 80 && this.zoomLevel <= 90) {
        factor = factor + 0.19;
      }
      else if (this.zoomLevel > 90 && this.zoomLevel <= 100) {
        factor = factor + 0.23;
      }
      let scaleFactor = 0.12 + factor;

      const button = document.getElementById('CustomClickHereToSignButton');
      if (button) {
        button.style.padding = '7px';
        button.style.width = "100px";
        button.style.fontSize = "11px";
        const buttonXPos = this.signCordinates.xPos * scaleFactor;
        const buttonYPos = this.signCordinates.yPos * scaleFactor;
        button.style.left = `${buttonXPos - 10}px`;
        button.style.bottom = `${buttonYPos - 3}px`;
      }
    }
  }

  /**
   * A table field carries all of its rows as a JSON array in a single value, while the PDF
   * lays them out as a grid of one widget per cell. Those widgets are not in the field map,
   * so flatten the array into "<widget> Row <n>" entries for attachFormFieldListeners to fill.
   */
  private expandTableIntoCells(field: PDFFieldMapConfig) {
    const map = PDF_TABLE_WIDGETS[field.businessName];
    const rows = this.parseTableRows(field.value);
    if (!map || !rows) {
      return;
    }

    // Every row the PDF lays out is covered, not just the saved ones, so a row that was
    // removed since the last save doesn't leave its old text sitting on the form.
    for (let index = 0; index < map.maxRows; index++) {
      Object.entries(map.columns).forEach(([property, widget]) => {
        const value = rows[index]?.[property];
        this.tableCellValues[`${widget} ${map.rowLabel} ${index + 1}`] =
          value === null || value === undefined ? '' : String(value);
      });
    }
  }

  /**
   * Rows may arrive as plain JSON or base64 — the form posts them encoded to get past the
   * gateway WAF, and whether they come back decoded depends on the backend. Accept both,
   * otherwise every cell in the PDF's table silently renders blank.
   */
  private parseTableRows(value: any): any[] | null {
    return parseEncodedJsonArray(value);
  }

  initCheckBoxes(config: KeyValuePair, field: PDFFieldMapConfig, isFieldRequired: boolean) {
    if (field.fieldType == "PDCheckBox") {
      field.childFields?.forEach((checkbox: PDFCheckBoxField) => {
        const id = checkbox.fieldId;
        this.formKeyMapping[checkbox.fieldId] = id;
        this.formBusinessMapping[id] = checkbox.fieldId;
        this.formFieldPrepulatedMapping[checkbox.fieldId] = field.isPrepopulated;
        this.formFieldRequiredMapping[checkbox.fieldId] = isFieldRequired;
        config[id] = [checkbox.value, null]
      });

      if (field.mandatory) {
        const checkboxElementId = field.businessName;
        if (!config[checkboxElementId]) {
          config[checkboxElementId] = ['', null];
        }
        if (!this.checkBoxFields[checkboxElementId]) {
          this.checkBoxFields[checkboxElementId] = [];
        }
        this.checkBoxFields[checkboxElementId].push(field);
        this.formBusinessMapping[checkboxElementId] = checkboxElementId;
      }
    }
  }

  initRadioButtons(config: KeyValuePair, field: PDFFieldMapConfig, isFieldRequired: boolean) {
    if (field.fieldType == "PDRadioButton") {
      field.childFields?.forEach((checkbox: PDFCheckBoxField) => {
        const id = checkbox.fieldId;
        this.formKeyMapping[checkbox.fieldId] = id;
        this.formBusinessMapping[id] = id;
        this.formFieldPrepulatedMapping[checkbox.fieldId] = field.isPrepopulated;
        this.formFieldRequiredMapping[checkbox.fieldId] = isFieldRequired;
        config[id] = [checkbox.value, null]
      });

      if (field.mandatory) {
        const checkboxElementId = field.businessName;
        if (!config[checkboxElementId]) {
          config[checkboxElementId] = ['', null];
        }

        if (!this.radioBoxFields[checkboxElementId]) {
          this.radioBoxFields[checkboxElementId] = [];
        }
        this.radioBoxFields[checkboxElementId].push(field);
        this.formBusinessMapping[checkboxElementId] = checkboxElementId;
      }
    }
  }

  onFormSubmission() {
    this.isPdfSubmitted = true;
    this.isSignButtonPlaced = false;
  }

  ngAfterViewInit() {
    if (this.pdfConfig) {
      setTimeout(async () => {
        await this.handleSign();
        if (this.pdfFile) {
          const response = await fetch(this.pdfFile);
          this.pdfBlob = await response.blob();
        }

        await this.attachQRCode();
      }, 1000);
    } else {
      this.isLoaded = true;
    }
  }

  attachFormFieldListeners(event: any) {
    setTimeout(() => {
      const inputFields = document.querySelectorAll(
        '.pdfViewer .annotationLayer input, .pdfViewer .annotationLayer select, .pdfViewer .annotationLayer textarea'
      );
      inputFields.forEach((inputField: any) => {
        inputField.setAttribute('aria-label', this.formKeyMapping[inputField.name]);
        inputField.setAttribute('tabindex', 0)
        if (this.processedFields.indexOf(inputField.name) == -1 || inputField.type == "checkbox" || inputField.type == "radio") {
          this.processedFields.push(inputField.name);
          if (this.signedField && inputField.name == this.signedField.fieldId) {
            inputField.style.display = 'none';
            return;
          }

          // Cells of a JSON table: the PDF has a widget per cell, but the field map only
          // knows the one field holding the whole array, so these are filled directly.
          if (this.tableCellValues[inputField.name] !== undefined) {
            inputField.value = this.tableCellValues[inputField.name];
            inputField.readOnly = true;
            return;
          }

          // That field itself is only a carrier — never print the raw JSON on the form.
          if (PDF_TABLE_WIDGETS[this.formKeyMapping[inputField.name]]) {
            inputField.value = '';
            inputField.readOnly = true;
            return;
          }

          const angularFieldName = this.form?.get([this.formKeyMapping[inputField.name]]);
          // if field exsit in form
          if (angularFieldName) {
            // register change event listner (works for all the fields) 
            if (this.formFieldPrepulatedMapping[inputField.name] != 'Y') {
              this.registerEventToInputField(inputField, angularFieldName)
            }

            //ignore for signature textbox
            if (inputField.type == 'text' && inputField.name != 'Signature of Employee') {
              if (this.formFieldPrepulatedMapping[inputField.name] == 'Y') {
                inputField.style.backgroundColor = '#b3e7ff';
              } else if (this.formFieldRequiredMapping[inputField.name]) {
                inputField.style.backgroundColor = '#FFEA00';
              }
            }

            // get the initial value and put it in form
            const angularValue = angularFieldName.value;
            const matchedHighlight = this.highlightedFields?.find(
              f => f.fieldId === inputField.name
            );
            if (angularValue) {
              // handle checkbox
              if (inputField.type == "checkbox") {
                const exportValue = inputField.getAttribute('exportvalue');
                inputField.checked = exportValue == angularValue ? true : false;
                if (matchedHighlight) {
                  inputField.style.backgroundColor = ''
                  if (matchedHighlight.op === 'add') {
                    inputField.style.backgroundColor = '#c6f7c6'
                    inputField.disabled = true;
                  } else if (matchedHighlight.op === 'replace') {
                    inputField.style.backgroundColor = '#FFD580'
                  } else {
                    inputField.style.backgroundColor = '#f99';  // 🔴 Highlight changed fields
                  }
                }
              }
              else if (inputField.type == "radio") {
                const exportValue = inputField.getAttribute('exportvalue');
                inputField.checked = exportValue == angularValue ? true : false;
                if (matchedHighlight) {
                  inputField.style.backgroundColor = ''
                  if (matchedHighlight.op === 'add') {
                    inputField.style.backgroundColor = '#c6f7c6'
                    inputField.disabled = true;
                  } else if (matchedHighlight.op === 'replace') {
                    inputField.style.backgroundColor = '#FFD580'
                  } else {
                    inputField.style.backgroundColor = '#f99';  // 🔴 Highlight changed fields
                  }
                }
              } else {
                inputField.value = angularValue;
                if (matchedHighlight) {
                  inputField.style.backgroundColor = ''
                  if (matchedHighlight.op === 'add') {
                    inputField.style.backgroundColor = '#c6f7c6'
                    inputField.disabled = true;
                  } else if (matchedHighlight.op === 'replace') {
                    inputField.style.backgroundColor = '#FFD580'
                  } else {
                    inputField.style.backgroundColor = '#f99';  // 🔴 Highlight changed fields
                  }
                }
              }
            }

            // Lock all inline fields in sign-only mode; only I-9 stays editable in the popup.
            if (this.readonly || this.SignOnly || this.formFieldPrepulatedMapping[inputField.name] == 'Y') {
              if (inputField instanceof HTMLInputElement) {
                if (inputField.type === 'checkbox' || inputField.type === 'radio') {
                  inputField.disabled = true;
                } else {
                  inputField.readOnly = true;
                }
              } else if (inputField instanceof HTMLTextAreaElement) {
                inputField.readOnly = true;
              } else if (inputField instanceof HTMLSelectElement) {
                inputField.disabled = true;
              }
            }
          }
        }
      });

      this.isLoaded = true;

      // Show "Click Here to Sign" on first-time sign, or when provider has no saved signature yet.
      // Re-sign after field edits on already-submitted forms is handled by the valueChanges handler.
      if (!this.readonly && (!this.isPdfSubmitted || (this.resignRequired && !this.signature))) {
        this.addSignHereButton(this.signCordinates, this.signField);
      }
    }, 1000);
  }

  registerEventToInputField(inputField: any, angularFieldName: any) {
    inputField.addEventListener('input', () => {
      this.handleEventFromInput(inputField, angularFieldName);
    });
  }

  handleEventFromInput(inputField: any, angularFieldName: any) {
    if (inputField.type == "checkbox") {
      const isChecked = inputField.checked;

      if (isChecked) {
        //check if checkbox exist in radio buttons
        Object.entries(this.radioBoxFields).forEach(([key, values]) => {
          if (values.length > 0) {
            const isTheValidFiled = values[0].childFields.filter((e: any) => e.fieldId == inputField.name);
            if (isTheValidFiled && isTheValidFiled.length > 0) {
              values[0].childFields.forEach((radio: any) => {
                if (radio.fieldId !== inputField.name) {
                  var formEle = this.form?.get(radio.fieldId);
                  if (formEle) {
                    formEle.setValue('Off');
                  }
                }
              });
            }
          }
        });
      }

      angularFieldName.markAsTouched();
      const exportValue = inputField.getAttribute('exportvalue');
      angularFieldName.setValue(isChecked ? exportValue : 'Off');
    } else if (inputField.type == "radio") {
      angularFieldName.markAsTouched();
      const exportValue = inputField.getAttribute('exportvalue');
      angularFieldName.setValue(exportValue);
    }
    else {
      angularFieldName.markAsTouched();
      angularFieldName.setValue(inputField.value);
    }
  }

  onPageRendered(event: any) {
    this.attachFormFieldListeners(event);
  }

  navigateToPdfList() {
    this.router.navigate(['/']);
  }

  /** Service Agreement form master ids: IC (164), Vendor (165) and IF(166). */
  private static readonly SERVICE_AGREEMENT_FORM_IDS = ['164', '165', '166'];

  /** pdfConfig.form_id is the checklist item's formMasterId, stringified by the callers. */
  private get isServiceAgreementForm(): boolean {
    return EditpdfComponent.SERVICE_AGREEMENT_FORM_IDS.includes(String(this.pdfConfig?.form_id));
  }

  /** Flag each signature field that has no signatureId yet as "<Type> has not signed yet". */
  private addUnsignedSignatureErrors(signatureFields: PDFFieldMapConfig[]): void {
    const unsignedErrors = signatureFields
      .filter((field: PDFFieldMapConfig) => !(field.coordinates as PDFSignatureCoordinates)?.signatureId)
      .map((field: PDFFieldMapConfig) => ({
        controlName: field.businessName,
        errorName: `${PROVIDER_TYPES[field.source!] || field.source} has not signed yet`,
        errorValue: `${PROVIDER_TYPES[field.source!] || field.source} has not signed yet`,
        businessName: field.businessName
      }));

    if (unsignedErrors.length > 0) {
      this.errors = [...this.errors, ...unsignedErrors];
      this.showErrors = true;
    }
  }

  /**
   * Source code of the party doing the signing. Mirrors how handleSign picks `currentSource`:
   * the participant flow signs as 'PTC', an explicit provider type signs as itself, and with
   * neither we fall back to whatever the first signature field declares.
   */
  private resolveSignerSource(): string | undefined {
    if (this.isParticipantFlow) return 'PTC';
    if (this.selectedProviderType) return this.selectedProviderType;
    return this.pdfConfig?.fields.find(
      (e: PDFFieldMapConfig) => e.fieldType === 'CustomSignature'
    )?.source;
  }

  validateForm() {
    const state = this.validationService.validateForm(
      this.form,
      this.checkBoxFields,
      this.radioBoxFields,
      this.formBusinessMapping,
      this.pdfConfig,
      this.ignorePartBValidationOfI9
    );
    this.showErrors = state.showErrors;
    this.errors = state.errors;
    this.customError = state.customError;

    // Add other signature validation for EMP source
    if ((this.selectedProviderType === 'EMP' || this.showOtherSignatures) && this.pdfConfig) {
      this.addUnsignedSignatureErrors(
        this.pdfConfig.fields.filter(
          (e: PDFFieldMapConfig) => e.source !== 'PTC' && e.source != this.selectedProviderType && e.fieldType == 'CustomSignature'
        )
      );
    }

    // Service Agreement only: the participant reviews and counter-signs, so the provider has to
    // have signed first. Keyed on the signer's own source rather than reusing the EMP filter
    // above — for a participant that filter excludes selectedProviderType, which *is* the
    // provider field being waited on, so it would never match.
    if (this.isParticipantFlow && this.isServiceAgreementForm && this.pdfConfig) {
      const signerSource = this.resolveSignerSource();
      this.addUnsignedSignatureErrors(
        this.pdfConfig.fields.filter(
          (e: PDFFieldMapConfig) => e.fieldType == 'CustomSignature' && e.source !== signerSource
        )
      );
    }
  }

  async handleSign() {
    if (!this.showSignature) {
      this.isLoaded = true;
      return;
    }

    // Always resolve sign coordinates so "Click Here to Sign" can be placed
    let currentSource: string | undefined;
    if (this.pdfConfig) {
      let coordinates: any;
      let field: any;
      if (this.isParticipantFlow) {
        currentSource = "PTC";
        const requiredFields = this.pdfConfig.fields.filter(e => e.source == "PTC" && e.fieldType == "CustomSignature");
        if (requiredFields.length > 0) {
          field = requiredFields[0];
          coordinates = field.coordinates;
        }
      }
      else if (!this.isParticipantFlow && !this.selectedProviderType) {
        const requiredFields = this.pdfConfig.fields.filter(e => e.fieldType == "CustomSignature");
        if (requiredFields.length > 0) {
          field = requiredFields[0];
          coordinates = field.coordinates;
          currentSource = field.source;
        }
      }
      else {
        currentSource = this.selectedProviderType;
        let requiredFields = this.pdfConfig.fields.filter(e => e.source == this.selectedProviderType && e.fieldType == "CustomSignature");
        // Fall back to any CustomSignature field when none matches the specific provider type
        if (requiredFields.length === 0) {
          requiredFields = this.pdfConfig.fields.filter(e => e.fieldType == "CustomSignature");
        }
        if (requiredFields.length > 0) {
          field = requiredFields[0];
          coordinates = field.coordinates;
        }
      }

      if (coordinates) {
        this.signCordinates = coordinates;
        this.signField = field;
      }
    }

    // Signature image placement requires an actual signature
    if (!this.signature) {
      this.isLoaded = true;
      return;
    }

    if (this.pdfBlob && this.pdfConfig) {
      if (!this.readonly) {
        if (this.signCordinates && this.pdfBlob) {
          try {
            await this.addImageToPdf(
              this.pdfBlob,
              this.signature,
              this.signCordinates.pageId,
              this.signCordinates.xPos,
              this.signCordinates.yPos,
              this.signCordinates.signatureWidth,
              this.signCordinates.signatureHeight,
              this.signCordinates.rotate
            );
          } catch (error) {
            console.log(error);
          }

          if (!this.resignRequired) {
            this.form?.get(this.signField.businessName)?.setValue(this.signatureId);
            this.signedField = this.signField;
          }
        } else {
          console.log("Coordinates couldn't be found for signature");
        }
      }
      else {
        if (this.signCordinates) {
          await this.addImageToPdf(
            this.pdfBlob,
            this.signature,
            this.signCordinates.pageId,
            this.signCordinates.xPos,
            this.signCordinates.yPos,
            this.signCordinates.signatureWidth,
            this.signCordinates.signatureHeight,
            this.signCordinates.rotate
          );

          this.form?.get(this.signField.businessName)?.setValue(this.signatureId);
          this.signedField = this.signField;
        } else {
          console.log("coordinates couldn't found to sign");
        }
      }

      // Fetch and place other users' signatures
      await this.placeOtherSignatures(currentSource);
    }
  }

  private async placeOtherSignatures(currentSource?: string) {
    if (!this.pdfConfig || !currentSource || !this.programCode || !this.pdfBlob) {
      return;
    }

    const otherSignatureFields = this.pdfConfig.fields.filter(
      e => e.source != currentSource && e.fieldType == "CustomSignature"
    );

    for (const field of otherSignatureFields) {
      const coords: any = field.coordinates;
      const signatureId = coords?.signatureId;
      if (!signatureId || !coords) {
        continue;
      }

      this.form?.get(field.businessName)?.setValue(signatureId, { emitEvent: false });

      try {
        const data: any = await this.referralDataService.getSignatureById(this.programCode, field.source === 'PTC' ? 'participant' : 'provider', signatureId).toPromise();
        if (data?.responsedata?.length) {
          const activeSign = data.responsedata.find((s: any) => s.isActive);
          if (activeSign?.signatureImageBase64) {
            const signatureImage = `data:image/png;base64,${activeSign.signatureImageBase64}`;
            await this.addImageToPdf(
              this.pdfBlob!,
              signatureImage,
              coords.pageId,
              coords.xPos,
              coords.yPos,
              coords.signatureWidth,
              coords.signatureHeight,
              coords.rotate
            );
          }
        }
      } catch (error) {
        console.log(`Error fetching signature for signatureId ${signatureId}:`, error);
      }
    }
  }

  async addImageToPDF(coordinates: any, image: any) {
    if (this.pdfBlob) {
      await this.addImageToPdf(
        this.pdfBlob,
        image,
        coordinates.pageId ?? 1,           // Page number where the image will be placed
        coordinates.xPos,         // X position (from the bottom-left of the page)
        coordinates.yPos,         // Y position (from the bottom-left of the page)
        coordinates.signatureWidth ?? coordinates.qrWidth,         // Width of the image
        coordinates.signatureHeight ?? coordinates.qrHeight,           // Height of the image
        coordinates.rotate
      );
    } else {
      console.log("blob is not initiated, can not add signature");
    }
  }

  async attachQRCode() {
    if (!this.QRCodeData) {
      console.log("No QR Code found");
      return;
    }

    const qrCodeField = this.pdfConfig?.fields.find((f: any) => f.fieldId == 'QR_CODE' && f.coordinates);

    if (!qrCodeField || !this.pdfBlob) {
      console.log("No QR Code field with coordinates found in the PDF configuration");
      return;
    }

    const url = await this.pdfImageService.addImageToSpecificPages(this.pdfBlob, this.QRCodeData, qrCodeField.coordinates?.pages ?? []);
    if (url) {
      this.pdfFile = url;
      this.pdfBlob = await fetch(url).then(r => r.blob());
      // Reassigning pdfFile makes the PDF viewer re-render, which destroys the previous
      // annotation layer and any "Click Here to Sign" button inside it. Reset the flag
      // so the next page-render pass re-adds the button via attachFormFieldListeners.
      this.isSignButtonPlaced = false;
    }
    this.isLoaded = true;
  }

  addSignHereButton(coordinates: any, field: any) {
    if (this.isSignButtonPlaced) {
      return;
    }

    if (!coordinates) {
      return;
    }

    const annotationLayer = document.querySelector(`.pdfViewer .page[data-page-number="${coordinates.pageId}"] .annotationLayer`);

    if (!annotationLayer) {
      console.log('annotation layer in specific page not found')
      return;
    }
    const existingButton = document.getElementById('CustomClickHereToSignButton');
    if (!existingButton) {
      const button = document.createElement('button');
      let scaleFactor = 0;
      if (this.viewport.isMobileView()) {
        scaleFactor = 0.52;
        button.style.padding = '7px';
        button.style.width = '100px';
        button.style.fontSize = 'xx-small';
      } else {
        scaleFactor = 1.34;
        button.style.padding = '13px';
        button.style.width = '140px';
      }

      const buttonXPos = coordinates.xPos * scaleFactor;
      const buttonYPos = coordinates.yPos * scaleFactor;

      button.innerText = "Click Here to Sign";
      button.id = "CustomClickHereToSignButton";
      button.style.position = 'absolute';
      button.style.left = `${buttonXPos - 10}px`;
      button.style.bottom = `${buttonYPos - 3}px`;
      button.style.zIndex = '2147483647'; // Ensure the button is above the PDF content
      button.style.background = 'repeating-linear-gradient(45deg, yellow, #f4f4ee 10px)';
      button.style.borderRadius = '5px';
      button.style.cursor = 'pointer';
      button.style.pointerEvents = 'auto';
      button.style.color = 'red';
      button.style.fontWeight = '700';
      button.style.border = '2px solid red';
      button.style.rotate = coordinates.rotate ? -coordinates.rotate + 'deg' : '0deg';

      // Add click event to the button to trigger the signature action
      button.addEventListener('click', () => {
        if (!this.signature) {
          this.errors = [{
            controlName: field,
            errorName: 'Signature not found, Please add your signature in Signature Tab',
            errorValue: 'Signature not found, Please add your signature in Signature Tab',
            businessName: this.formBusinessMapping[field]
          }];
          this.fieldsUpdated.emit({ data: null, errors: this.errors, customError: '' });
          return;
        }

        // Hide the button once clicked
        button.style.display = 'none';

        // Set the form value for the signature field
        this.form?.get(field.businessName)?.setValue(this.signatureId);
        if (field.signatureDateFieldId) {
          this.form?.get(field.signatureDateFieldId)?.setValue(this.getTodayDate());

          const signField = document.querySelectorAll(`.pdfViewer .annotationLayer input[name="${field.signatureDateFieldId}"]`);
          if (signField.length) {
            (signField[0] as HTMLInputElement).value = this.getTodayDate();
          }
        }
        this.signedField = field;
      });

      if (!this.isSignButtonPlaced) {
        // Append the button to the annotation layer
        annotationLayer?.appendChild(button);
        this.isSignButtonPlaced = true;
        this.updateButtonPosition();
      }
    } else {
      existingButton.style.display = 'block';
      this.isSignButtonPlaced = true;
      this.updateButtonPosition();
    }
  }

  async addImageToPdf(pdfFile: Blob, content: any, pageNumber: number, xPos: number, yPos: number, width: number, height: number, rotate: number = 0) {
    const url = await this.pdfImageService.addImageToPdf(pdfFile, content, pageNumber, xPos, yPos, width, height, rotate);
    if (url) {
      this.pdfFile = url;
      this.pdfBlob = await fetch(url).then(r => r.blob());
    }
    this.isLoaded = true;
  }

  getTodayDate(): string {
    return this.validationService.getTodayDate();
  }
}